/* ─────────────────────────────────────────────────────────────
   VIP Store Locator – frontend logic
   Talks ONLY to your backend at BACKEND_URL.
   The backend signs and forwards requests to the VIP API.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // Backend URL — your deployed Render service.
  // For local testing, change to 'http://localhost:3000'.
  const BACKEND_URL = 'https://vip-store-locator-api.onrender.com';

  const LIST_PAGE_SIZE = 8;       // how many results show in the list at once
  const API_PAGE_SIZE = 400;      // max results to pull from API (loads all markers)

  // ── Online retailers (Buy Online tab) ──────────────────────
  // Edit this list to add/remove/reorder online retailers.
  // Each entry can later swap to a real logo by adding a `logo` URL —
  // the renderer prefers `logo` over `monogram` if present.
  //
  // Fields:
  //   id        unique slug (used for tracking / future analytics)
  //   name      display name
  //   tagline   short descriptor under the name
  //   url       opens in a new tab on click
  //   monogram  { letters, bg, fg } — used until a real logo is added
  //   logo      OPTIONAL string URL — when present, replaces monogram
  const RETAILERS = [
    {
      id: 'totalwine',
      name: 'Total Wine',
      tagline: 'Ship or pickup',
      url: 'https://www.totalwine.com/beer/brand/arizona-hard-tea',
      monogram: { letters: 'TW', bg: '#002E5D', fg: '#ffffff' },
      // logo: 'https://cdn.shopify.com/.../totalwine.png',
    },
    {
      id: 'bevmo',
      name: 'BevMo!',
      tagline: 'Ship or pickup',
      url: 'https://bevmo.com/search?q=arizona+hard',
      monogram: { letters: 'B!', bg: '#003DA5', fg: '#ffffff' },
    },
    {
      id: 'instacart',
      name: 'Instacart',
      tagline: 'Same-day delivery',
      url: 'https://www.instacart.com/store/s?k=arizona+hard+tea',
      monogram: { letters: 'i', bg: '#43B02A', fg: '#ffffff' },
    },
    {
      id: 'target',
      name: 'Target',
      tagline: 'Pickup or delivery',
      url: 'https://www.target.com/b/arizona-hard-tea/-/N-q643leazbys',
      monogram: { letters: 'T', bg: '#CC0000', fg: '#ffffff' },
    },
    {
      id: 'safeway',
      name: 'Safeway',
      tagline: 'Pickup or delivery',
      url: 'https://www.safeway.com/shop/search-results.html?q=arizona+hard+tea',
      monogram: { letters: 'S', bg: '#E11A2C', fg: '#ffffff' },
    },
    {
      id: 'gopuff',
      name: 'Gopuff',
      tagline: 'Fast local delivery',
      url: 'https://www.gopuff.com/go/search?query=arizona+hard',
      monogram: { letters: 'G', bg: '#7B2CBF', fg: '#ffffff' },
    },
    {
      id: 'minibar',
      name: 'Minibar',
      tagline: 'Local delivery',
      url: 'https://minibardelivery.com/store/search?q=arizona+hard',
      monogram: { letters: 'M', bg: '#FFD700', fg: '#1a1a1a' },
    },
    {
      id: 'ubereats',
      name: 'Uber Eats',
      tagline: 'On-demand delivery',
      url: 'https://www.ubereats.com/search?q=arizona%20hard%20tea',
      monogram: { letters: 'U', bg: '#000000', fg: '#06C167' },
    },
  ];

  // ── DOM refs ───────────────────────────────────────────────
  const form        = document.getElementById('searchForm');
  const zipInput    = document.getElementById('zipInput');
  const milesInput  = document.getElementById('milesInput');
  const brandInput  = document.getElementById('brandInput');
  const stInput     = document.getElementById('storeTypeInput');
  const searchBtn   = document.getElementById('searchBtn');
  const status      = document.getElementById('statusArea');
  const resultsList = document.getElementById('resultsList');
  const pagination  = document.getElementById('pagination');
  const prevBtn     = document.getElementById('prevPageBtn');
  const nextBtn     = document.getElementById('nextPageBtn');
  const pageInfo    = document.getElementById('pageInfo');

  // Tab + Buy Online refs (added in HTML)
  const locatorEl   = document.getElementById('storeLocator');
  const tabsEl      = document.getElementById('locatorTabs');
  const onlineGrid  = document.getElementById('onlineGrid');

  // ── State ──────────────────────────────────────────────────
  let map;
  let markersLayer;
  let currentResults = [];     // ALL results from the most recent search
  let currentListPage = 0;     // which slice of the list we're showing
  let lastQuery = null;
  let currentTab = 'stores';   // 'stores' | 'online'

  // ── Custom marker icon ─────────────────────────────────────
  // Marker URL is read from the data-marker-url attribute on
  // <div id="storeLocator">, set by Liquid in the Custom Liquid block:
  //   <div class="locator" id="storeLocator" data-marker-url="{{ 'marker.png' | asset_url }}">
  // Shopify's asset_url returns a protocol-relative URL (starting with //),
  // which Leaflet rejects — so we prepend https: if needed.
  let markerUrl = (locatorEl && locatorEl.dataset.markerUrl) || '';
  if (markerUrl.startsWith('//')) markerUrl = 'https:' + markerUrl;
  const markerIcon = L.icon({
    iconUrl: markerUrl,
    iconSize: [52, 49],
    iconAnchor: [26, 46],
    popupAnchor: [0, -42],
  });

  // ── Map setup ──────────────────────────────────────────────
  function initMap() {
    map = L.map('map', { zoomControl: true, scrollWheelZoom: false })
           .setView([39.8283, -98.5795], 4);   // Center of continental US

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);

    markersLayer = L.layerGroup().addTo(map);

    // Re-enable scroll-zoom only after user clicks the map (good UX inside a page)
    map.on('click', () => map.scrollWheelZoom.enable());
  }

  // ── Tab switching ──────────────────────────────────────────
  // Switches the visible view via a data-tab attribute on the locator.
  // CSS does the actual hiding/showing — JS just flips state and aria.
  // The map element stays in the DOM so Leaflet's tile cache is preserved
  // and switching back to Stores is instant.
  function setTab(tab, opts) {
    if (tab !== 'stores' && tab !== 'online') tab = 'stores';
    currentTab = tab;
    locatorEl.dataset.tab = tab;

    // Update aria + visual state on the tab buttons
    if (tabsEl) {
      tabsEl.querySelectorAll('.locator-tab').forEach(btn => {
        const isActive = btn.dataset.tab === tab;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    // Render the online grid lazily on first switch
    if (tab === 'online' && onlineGrid && !onlineGrid.dataset.rendered) {
      renderRetailers();
      onlineGrid.dataset.rendered = 'true';
    }

    // Reflect in URL so the tab is shareable/linkable.
    // Skip when we're loading from the URL on init (opts.fromUrl).
    if (!opts || !opts.fromUrl) {
      const url = new URL(window.location.href);
      if (tab === 'online') url.searchParams.set('tab', 'buy-online');
      else url.searchParams.delete('tab');
      history.replaceState(null, '', url.toString());
    }

    // Leaflet needs a nudge after its container changes size (e.g., the
    // user switched away then back). invalidateSize is a no-op on first
    // load, so it's safe to call every time.
    if (tab === 'stores' && map) {
      // setTimeout 0 lets the CSS transition settle before Leaflet measures
      setTimeout(() => map.invalidateSize(), 0);
    }
  }

  function readTabFromUrl() {
    const param = new URLSearchParams(window.location.search).get('tab');
    return param === 'buy-online' ? 'online' : 'stores';
  }

  // ── Render Buy Online retailer grid (one-time) ─────────────
  function renderRetailers() {
    if (!onlineGrid) return;

    onlineGrid.innerHTML = RETAILERS.map(r => {
      const visual = r.logo
        ? `<img class="retailer-card__logo" src="${escapeAttr(r.logo)}" alt="${escapeAttr(r.name)} logo" loading="lazy">`
        : `<span class="retailer-card__monogram"
                 style="background:${escapeAttr(r.monogram.bg)};color:${escapeAttr(r.monogram.fg)};"
                 aria-hidden="true">${escapeHtml(r.monogram.letters)}</span>`;

      return `
        <a class="retailer-card"
           href="${escapeAttr(r.url)}"
           target="_blank"
           rel="noopener noreferrer"
           data-retailer-id="${escapeAttr(r.id)}">
          ${visual}
          <span class="retailer-card__text">
            <span class="retailer-card__name">${escapeHtml(r.name)}</span>
            <span class="retailer-card__tagline">${escapeHtml(r.tagline)}</span>
          </span>
        </a>
      `;
    }).join('');
  }

  // ── Brand list (cached on the backend) ─────────────────────
  async function loadBrands() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/brands`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Brands dropdown
      (data.brands || []).forEach(brand => {
        const opt = document.createElement('option');
        opt.value = brand;
        opt.textContent = brand;
        brandInput.appendChild(opt);
      });

      // Specific store types appended after the on/off generic options
      (data.locations || []).forEach(loc => {
        const opt = document.createElement('option');
        opt.value = loc.code;
        opt.textContent = loc.description;
        stInput.appendChild(opt);
      });
    } catch (err) {
      console.warn('Could not load brand list:', err);
      // Non-fatal: search still works, brand dropdown just stays empty
    }
  }

  // ── Search ─────────────────────────────────────────────────
  // Loads ALL matching results in a single API call so every store appears
  // on the map. The list shows them in chunks of LIST_PAGE_SIZE.
  async function doSearch() {
    // If user submits search while on Buy Online tab, jump them to Stores.
    if (currentTab !== 'stores') setTab('stores');

    const zip = zipInput.value.trim();
    if (!/^\d{5}$/.test(zip)) {
      showError('Please enter a valid 5-digit ZIP code.');
      zipInput.focus();
      return;
    }

    const params = new URLSearchParams({
      zip,
      miles: milesInput.value,
      page: '0',
      pagesize: String(API_PAGE_SIZE),
    });
    if (brandInput.value) params.set('brand', brandInput.value);
    if (stInput.value)    params.set('storeType', stInput.value);

    lastQuery = params.toString();
    setLoading(true);
    showStatus('Searching…');

    try {
      const res = await fetch(`${BACKEND_URL}/api/results?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      currentResults = data.locations || [];
      currentListPage = 0;

      renderMarkers();   // draw ALL markers once
      renderListPage();  // render the first chunk in the list

      if (!currentResults.length) {
        showStatus('No stores found in this area. Try widening your search radius.');
      }
    } catch (err) {
      console.error(err);
      showError(`Search failed: ${err.message}`);
      clearMarkers();
      resultsList.innerHTML = '';
      if (pagination) pagination.hidden = true;
    } finally {
      setLoading(false);
    }
  }

  // ── Draw all markers on the map (called once per search) ───
  function renderMarkers() {
    // Defensive: if map didn't initialize for some reason, do it now
    if (!markersLayer) initMap();

    clearMarkers();
    const bounds = [];

    currentResults.forEach((loc, i) => {
      const lat = parseFloat(loc.lat);
      const lng = parseFloat(loc.long);
      if (!isFinite(lat) || !isFinite(lng)) return;

      const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(markersLayer);
      marker.bindPopup(buildPopupHtml(loc));
      marker._idx = i;
      bounds.push([lat, lng]);
    });

    if (bounds.length) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }

  // ── Render the current page-slice of the results list ──────
  function renderListPage() {
    resultsList.innerHTML = '';

    if (!currentResults.length) {
      if (pagination) pagination.hidden = true;
      return;
    }

    const total = currentResults.length;
    const totalListPages = Math.ceil(total / LIST_PAGE_SIZE);
    const startIdx = currentListPage * LIST_PAGE_SIZE;
    const endIdx = Math.min(startIdx + LIST_PAGE_SIZE, total);
    const slice = currentResults.slice(startIdx, endIdx);

    showStatus(`Showing ${startIdx + 1}–${endIdx} of ${total} store${total === 1 ? '' : 's'}`);

    slice.forEach((loc, n) => {
      const i = startIdx + n;

      const li = document.createElement('li');
      li.className = 'result-card';
      li.dataset.idx = i;

      // Determine store-type label from the COT rollup
      const rollup = (loc.storeTypeRollup || '').toLowerCase();
      let typeTag = '';
      if (rollup === 'on') typeTag = '<span class="tag">Bar / Restaurant</span>';
      else if (rollup === 'off') typeTag = '<span class="tag">Retail</span>';

      const phoneHtml = loc.phoneFormatted
        ? `<span class="result-card__phone">${escapeHtml(loc.phoneFormatted)}</span>`
        : '';

      // Build a Google Maps URL using the full address (works on desktop + mobile)
      const fullAddress = [loc.street, loc.city, loc.state, loc.zip]
        .filter(Boolean).join(', ');
      const mapsUrl = 'https://www.google.com/maps/search/?api=1&query=' +
        encodeURIComponent(`${loc.dba || ''} ${fullAddress}`.trim());

      li.innerHTML = `
        <div class="result-card__head">
          <a href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener"
             class="result-card__head-text result-card__link"
             title="Open in Google Maps">
            <p class="result-card__name">${escapeHtml(loc.dba || 'Unnamed location')}</p>
            <p class="result-card__addr">
              ${escapeHtml(loc.street || '')}<br>
              ${escapeHtml(loc.city || '')}, ${escapeHtml(loc.state || '')} ${escapeHtml(loc.zip || '')}
            </p>
          </a>
          <span class="result-card__distance">${escapeHtml(String(loc.distance || '?'))} mi</span>
        </div>
        ${(typeTag || phoneHtml) ? `<div class="result-card__meta">${typeTag}${phoneHtml}</div>` : ''}
      `;

      // Card click zooms the map — but if the user clicked the link itself,
      // let the link's default behavior (open Google Maps) take over.
      li.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;   // user clicked the link; do nothing extra
        focusLocation(i);
      });
      resultsList.appendChild(li);
    });

    // Pagination UI (for the list only — markers stay put).
    // Skip silently if any pagination DOM elements aren't present.
    if (pagination && pageInfo && prevBtn && nextBtn) {
      if (totalListPages > 1) {
        pagination.hidden = false;
        pageInfo.textContent = `Page ${currentListPage + 1} of ${totalListPages}`;
        prevBtn.disabled = currentListPage === 0;
        nextBtn.disabled = currentListPage >= totalListPages - 1;
      } else {
        pagination.hidden = true;
      }
    }

    // Scroll the list back to the top so the first card is visible
    resultsList.scrollTop = 0;
  }

  function focusLocation(idx) {
    document.querySelectorAll('.result-card').forEach(el => el.classList.remove('is-active'));
    const card = resultsList.querySelector(`[data-idx="${idx}"]`);
    if (card) {
      card.classList.add('is-active');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    const loc = currentResults[idx];
    if (loc) {
      const lat = parseFloat(loc.lat);
      const lng = parseFloat(loc.long);
      map.setView([lat, lng], 15);
      markersLayer.eachLayer(m => {
        if (m._idx === idx) m.openPopup();
      });
    }
  }

  function buildPopupHtml(loc) {
    const fullAddress = [loc.street, loc.city, loc.state, loc.zip]
      .filter(Boolean).join(', ');
    const mapsUrl = 'https://www.google.com/maps/search/?api=1&query=' +
      encodeURIComponent(`${loc.dba || ''} ${fullAddress}`.trim());

    return `
      <a href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener" class="popup-link">
        <strong>${escapeHtml(loc.dba || 'Unnamed location')}</strong><br>
        ${escapeHtml(loc.street || '')}<br>
        ${escapeHtml(loc.city || '')}, ${escapeHtml(loc.state || '')} ${escapeHtml(loc.zip || '')}
      </a><br>
      ${loc.phoneFormatted ? `<a href="tel:${escapeHtml(loc.phone || '')}">${escapeHtml(loc.phoneFormatted)}</a><br>` : ''}
      <span class="popup-distance">${escapeHtml(String(loc.distance || '?'))} miles away</span>
    `;
  }

  function clearMarkers() {
    if (markersLayer) markersLayer.clearLayers();
  }

  // ── UI helpers ─────────────────────────────────────────────
  function setLoading(isLoading) {
    searchBtn.disabled = isLoading;
    searchBtn.classList.toggle('btn--loading', isLoading);
  }
  function showStatus(msg) {
    status.classList.remove('is-error');
    status.textContent = msg;
  }
  function showError(msg) {
    status.classList.add('is-error');
    status.textContent = msg;
  }
  function escapeHtml(s) {
    // First decode any HTML entities the API may have already encoded
    // (e.g. "STOP &amp; SHOP" -> "STOP & SHOP"), then re-encode safely.
    // This way both raw and pre-encoded inputs render correctly.
    const decoded = decodeEntities(String(s));
    return decoded.replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  // Same as escapeHtml but explicitly for use inside HTML attribute values
  // (URLs, colors, IDs we control). Kept separate for readability.
  function escapeAttr(s) {
    return escapeHtml(s);
  }

  // Decode HTML entities like &amp; &lt; &#39; into their real characters.
  // Uses a detached <textarea> which the browser parses entities into safely.
  let _decoder;
  function decodeEntities(s) {
    if (!_decoder) _decoder = document.createElement('textarea');
    _decoder.innerHTML = s;
    return _decoder.value;
  }

  // ── Event wiring ───────────────────────────────────────────
  form.addEventListener('submit', e => {
    e.preventDefault();
    doSearch();
  });
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (currentListPage > 0) {
        currentListPage--;
        renderListPage();
      }
    });
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      const totalListPages = Math.ceil(currentResults.length / LIST_PAGE_SIZE);
      if (currentListPage < totalListPages - 1) {
        currentListPage++;
        renderListPage();
      }
    });
  }

  // Tab clicks (event-delegated so it works whether 1 or N tabs)
  if (tabsEl) {
    tabsEl.addEventListener('click', e => {
      const btn = e.target.closest('.locator-tab');
      if (!btn) return;
      setTab(btn.dataset.tab);
    });
    // Keyboard: ←/→ to switch tabs when one is focused
    tabsEl.addEventListener('keydown', e => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tabs = Array.from(tabsEl.querySelectorAll('.locator-tab'));
      const idx = tabs.findIndex(t => t === document.activeElement);
      if (idx === -1) return;
      const next = e.key === 'ArrowRight'
        ? tabs[(idx + 1) % tabs.length]
        : tabs[(idx - 1 + tabs.length) % tabs.length];
      next.focus();
      setTab(next.dataset.tab);
    });
  }

  // ── Init ───────────────────────────────────────────────────
  function init() {
    initMap();
    loadBrands();
    // Honor ?tab=buy-online deep links (marketing emails, social posts)
    setTab(readTabFromUrl(), { fromUrl: true });
  }
  // Because the script is loaded with defer, DOMContentLoaded may have
  // already fired by the time we get here. Handle both cases.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
